import { groupLabelCode } from "@bt/domain";
import { useMutation } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { PlusIcon, ShoppingBasketIcon, XIcon } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { EmptyState } from "@/components/app/empty-state";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/misc";
import { useTRPC } from "@/lib/trpc";
import { PrintPanel } from "./print-panel";
import { AddBlankDialog, type LabelItem, LabelRow, useShowError } from "./shared";

/** Labels › Basket: what will be printed, in print order, and the print page beside it. */
export function BasketTab({ basket }: { basket: LabelItem[] | undefined }) {
  const { t } = useTranslation();
  const trpc = useTRPC();
  const showError = useShowError();
  const [adding, setAdding] = useState(false);
  const remove = useMutation({
    ...trpc.labels.removeFromBasket.mutationOptions(),
    onError: showError,
  });
  if (basket === undefined) return <Skeleton className="h-64 rounded-xl" />;
  const add = (
    <Button variant="outline" onClick={() => setAdding(true)}>
      <PlusIcon />
      {t("labels.addBlank")}
    </Button>
  );
  return (
    <>
      {basket.length === 0 ? (
        <EmptyState
          icon={ShoppingBasketIcon}
          title={t("labels.basketEmpty")}
          body={t("labels.basketEmptyBody")}
          action={
            <>
              {add}
              <Button asChild variant="outline">
                <Link to="/inventory">{t("nav.inventory")}</Link>
              </Button>
            </>
          }
        />
      ) : (
        <div className="grid grid-cols-[minmax(0,1fr)] items-start gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)]">
          <section className="flex min-w-0 flex-col gap-3" aria-label={t("labels.basket")}>
            <div className="flex items-center justify-between gap-3">
              <p className="text-sm text-muted-foreground">{t("labels.basketHint")}</p>
              {add}
            </div>
            <Card className="divide-y divide-border">
              {basket.map((label, i) => (
                <LabelRow
                  key={label.code}
                  label={label}
                  position={i + 1}
                  actions={
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={t("labels.removeCode", { code: groupLabelCode(label.code) })}
                      disabled={remove.isPending}
                      onClick={() => remove.mutate({ code: label.code })}
                    >
                      <XIcon />
                    </Button>
                  }
                />
              ))}
            </Card>
          </section>
          <PrintPanel basket={basket} />
        </div>
      )}
      <AddBlankDialog open={adding} onOpenChange={setAdding} />
    </>
  );
}
